import frame from "../generated/frame.json";
import {
  defaultText,
  type Face,
  fontOf,
  fontStyle,
  isFace,
  type PageText,
  pageTextOf,
  textStyle,
} from "../shared/fonts";
import type { UnknownKeys } from "../shared/frames";
import { type OwnIcon, PICTURES_PATH, pageIcon } from "../shared/icons";
import type { Layout } from "../shared/layouts";
import { noop } from "../shared/quietly";
import { SELECTOR_LENGTH } from "../shared/selector";
import { ASSETS_PATH } from "../shared/views/assets";
import { readManifest } from "../shared/views/manifest";
import type { Participant, Selection, ViewConnection } from "./connection";
import type { DataHost, Person } from "./data-host";
import {
  type DataChange,
  type DateFormat,
  DRAWN_WAIT_MS,
  type FrameBoot,
  type FrameCall,
  type FrameData,
  type FrameNotice,
  type HostPresentation,
  type NavEntry,
  type PageInit,
  type Picked,
  type Reach,
  type Seen,
} from "./frame/protocol";
import { FrameChannel } from "./frame-channel";
import { OutsideLinks, resolveLink, shareLink, strings } from "./links";
import { withStock } from "./stock";

/** Who the viewer is to the view: their person's id, the same in every chat, and the name and color they go by. */
export interface Identity {
  id: string;
  name: string;
  color: string;
}
/**
 * Where a view opens, beyond what its object says: in a chat, with what the Coast app knows of the chat's title and icon
 * and its organization, or at its home (`scope: 'home'`), with the chats it reads (docs/views.md#1-views).
 */
export interface PageContext {
  view: string;
  chat: { title: string; icon: OwnIcon | null } | null;
  org: { id: string; name: string } | null;
  scope?: "chat" | "home";
  name?: string;
  chats?: { id: string; title: string; icon: OwnIcon | null; member: boolean }[];
  /** How the host presents a section opened deeper, in a page of its own (`view.screen`): a screen, a sheet, or neither. */
  presents?: HostPresentation[];
  /** A view in a message reaches less than its manifest declares (`Reach`); anything else, all of it. */
  reach?: Reach;
}
/** What the page asks of whatever shows it: its screens, its title, a person's card, or a link that leaves it. */
export type PageRequest =
  | { type: "view.nav"; stack: NavEntry[] }
  | { type: "view.screen"; stack: NavEntry[]; presentation: HostPresentation }
  | { type: "view.home" }
  | { type: "view.back" }
  | { type: "view.title"; text: string; subtitle?: string }
  | { type: "view.person"; id: string; line: PersonLine | null }
  | { type: "view.open"; url: string };
// Everything a request carries is something the Coast app reads it by (shared/frames.ts), which this fails to compile without.
const requestsCarryKnownKeys: [UnknownKeys<PageRequest>] extends [never] ? true : never = true;
void requestsCarryKnownKeys;
/** Where `coast.live` goes: the presence on the view's connection. */
export type LiveConnection = Pick<ViewConnection, "setLive" | "sendLive">;

/** What the renderer shows: a view's source, whose `source` event says it changed, and which connection this is among the people present. */
export interface ViewDocument extends EventTarget {
  readonly source: string;
  readonly clientID: string;
}
/** A source held here, for the read-only render page and tests: no connection, and nobody else present. */
export class LocalView extends EventTarget implements ViewDocument {
  readonly clientID = "";
  constructor(public source: string) {
    super();
  }
  /** Another source, as an edit's reload brings one. */
  replace(source: string) {
    this.source = source;
    this.dispatchEvent(new Event("source"));
  }
}
/** A runtime error inside the view's page, as `onPageError` receives it. */
export interface PageError {
  kind: "error" | "rejection" | "console" | "load";
  message: string;
  source?: string;
  line?: number;
}

const SECTION = /^[a-z][a-z0-9_-]{0,39}$/;
/** A stack of sections from the page, as the Coast app takes it: at most 20, each named as a section is. */
function stackOf(value: unknown): NavEntry[] {
  return (Array.isArray(value) ? value : [])
    .slice(0, 20)
    .filter((entry) => SECTION.test(entry?.name))
    .map((entry) => ({
      name: entry.name,
      params: strings(entry.params),
      ...(entry.title ? { title: String(entry.title).slice(0, 80) } : {}),
      ...(typeof entry.id === "string" && ENTRY_ID.test(entry.id) ? { id: entry.id } : {}),
    }));
}
/** A visit's id, as the page makes them. */
const ENTRY_ID = /^[A-Za-z0-9_-]{1,24}$/;
/** The view's line on a person's card (`coast.people.open`, docs/views.md#6-the-page-api): what it says and the section it opens. */
export interface PersonLine {
  text: string;
  section: string;
  params: Record<string, string>;
}
/** A line from the page, as the Coast app takes it: short text and one of the view's sections by name, or nothing. */
export function personLineOf(value: unknown): PersonLine | null {
  if (!value || typeof value !== "object") return null;
  const line = value as Record<string, unknown>;
  const text = typeof line.text === "string" ? line.text.trim().slice(0, 80) : "";
  if (!text || typeof line.section !== "string" || !SECTION.test(line.section)) return null;
  return { text, section: line.section, params: strings(line.params) };
}
const LIVE_TYPE = /^[A-Za-z0-9_.:-]{1,40}$/;
/** `coast.live` state and messages ride on presence, which the view's object caps, so each stays small. */
function liveValue(value: unknown): unknown {
  const text = JSON.stringify(value ?? null);
  if (text === undefined || text.length > 1024) throw new Error("coast.live takes JSON of up to 1 KB.");
  return JSON.parse(text);
}

const isColor = (value: unknown): value is string => typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value);
/**
 * The look of the controls the Coast app floats over a view (docs/views.md#3-manifest): dark or light where its manifest
 * fixes it, for a view that is dark water or white paper in both themes, and otherwise what its page looks like.
 */
export const controlsLook = (fixed: "auto" | "dark" | "light" | undefined, page: "dark" | "light" | null) =>
  fixed === "dark" || fixed === "light" ? fixed : page;
/**
 * The view's name as the runtime reports it to the Coast app in `view.status`, which shows it under each screen's title:
 * its manifest's, else its template's, and never a template's id, which for a view made from its own source is `custom`.
 */
export const viewTitle = (manifestName: string | undefined, template: { name: string } | undefined) =>
  manifestName || template?.name || "Coast";
const WRITES = ["insert", "update", "put", "remove"] as const;
const EMPTY: FrameData = {
  version: 0,
  ready: false,
  chat: null,
  chats: [],
  writer: null,
  parts: {},
  rules: {},
  sections: {},
  queries: {},
  actions: {},
  uses: {},
  links: null,
};
const refuse = (message: string, code = "unavailable") => Object.assign(new Error(message), { code });
/**
 * Whether the person just tapped or pressed a key in the view: a tap in the page's frame activates the runtime's page
 * too, which is what the runtime trusts, never what the frame says.
 */
const tapped = () =>
  (navigator as Navigator & { userActivation?: { isActive: boolean } }).userActivation?.isActive !== false;
/** The calls that change something as the viewer, which a view in a message or a question makes only after a tap. */
const ACTS: ReadonlySet<string> = new Set<FrameCall>(["write", "call", "post", "respond"]);
const finite = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? Math.max(-1e6, Math.min(1e6, value)) : 0;
/** What the frame says of an element, kept to the sizes and shapes it may have; anything else is nothing. */
function seenOf(input: unknown): Seen | null {
  if (!input || typeof input !== "object") return null;
  const value = input as Record<string, unknown>,
    rect = (value.rect && typeof value.rect === "object" ? value.rect : {}) as Record<string, unknown>;
  const text = (item: unknown, max: number) => (typeof item === "string" ? item.slice(0, max) : "");
  return {
    rect: {
      x: finite(rect.x),
      y: finite(rect.y),
      width: Math.max(0, finite(rect.width)),
      height: Math.max(0, finite(rect.height)),
    },
    tag: typeof value.tag === "string" && /^[a-z][a-z0-9-]{0,40}$/.test(value.tag) ? value.tag : "element",
    text: text(value.text, 600),
    label: text(value.label, 120),
  };
}
/** A selector as a frame reports one, or as a selection carries it: one line, of a length. */
const isSelector = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0 && value.length <= SELECTOR_LENGTH && !/[\r\n]/.test(value);
function pickedOf(input: unknown): Picked | null {
  const seen = seenOf(input),
    value = input as Record<string, unknown>;
  if (!seen || !isSelector(value.selector)) return null;
  const extra = (key: string, max: number) =>
    typeof value[key] === "string" && value[key] ? { [key]: (value[key] as string).slice(0, max) } : {};
  return { ...seen, selector: value.selector, ...extra("id", 120), ...extra("classes", 200), ...extra("role", 40) };
}
/**
 * The policy a view's page starts with (docs/ideas/app-isolation.md): scripts, styles, and data from the page itself
 * and the origins its manifest declares as `network`, fonts from the view's server too, images from any view's assets
 * there (src/server/assets.ts), whose addresses are their own keys, so one upload serves every view of a chat, Coast's
 * map files and their tiles for kit.map (MAP_ORIGINS), and nothing else. No frames, no form leaves, no `<base>`.
 */
export function pagePolicy(network: readonly string[], server: string): string {
  const from = network.length ? ` ${network.join(" ")}` : "";
  const assets = ` ${server}${ASSETS_PATH}`;
  const maps = ` ${server}${MAPS_PATH} ${MAP_ORIGINS.join(" ")}`;
  return [
    "default-src 'none'",
    `script-src 'unsafe-inline' 'unsafe-eval' 'wasm-unsafe-eval' ${server}${MAPS_PATH}${from}`,
    `style-src 'unsafe-inline' ${server}${MAPS_PATH}${from}`,
    `img-src data: blob: ${server}${PICTURES_PATH}${assets}${maps}${from}`,
    `media-src data: blob:${from}`,
    `font-src data: ${server}${from}`,
    `connect-src${maps}${from}`,
    "worker-src blob:",
    "frame-src 'none'",
    "form-action 'none'",
    "base-uri 'none'",
  ].join("; ");
}
/** Where the views' server serves Coast's map files (scripts/maps/build.ts), which every view's page may load. */
export const MAPS_PATH = "/maps/";
/**
 * The origins a map's tiles come from, which every view's page may load, posted views too: OpenFreeMap's vector tiles
 * and Natural Earth's relief (docs/views.md#8-isolation).
 */
export const MAP_ORIGINS = ["https://tiles.openfreemap.org"] as const;
/**
 * What every page starts with, its own styles after: it holds still at its ends rather than pulling away, as the Coast
 * app's page itself does, so a sheet dragged down moves with its page in it; and it never scrolls sideways, so
 * something wider than the screen, such as a picture at its own size, is cut off at the edge rather than letting the
 * whole page slide (2026-10-21). `clip` makes no scroll container, so `position: sticky` still holds to the screen. A
 * view that wants the bounce sets `overscroll-behavior` itself, and one that scrolls sideways sets `overflow-x`.
 */
const PAGE_STYLE = "<style data-coast-page>html{overscroll-behavior-y:none}html,body{overflow-x:clip}</style>";
const attribute = (text: string) => text.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
/** What the frame shows once Coast has stopped a view that left its page. */
const STOPPED = `<!doctype html><meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'"><p style="font:14px -apple-system,system-ui,sans-serif;color:#8e8e93;margin:32px 24px;text-align:center">This view tried to open another page in its place, so Coast stopped it.</p>`;
const embed = (value: unknown) =>
  JSON.stringify(value)
    .replace(/</g, "\\u003c")
    .replace(new RegExp(String.fromCharCode(8232), "g"), "\\u2028")
    .replace(new RegExp(String.fromCharCode(8233), "g"), "\\u2029");

/** A date format from the Coast app, or null for one it doesn't make. */
export function dateFormat(value: unknown): DateFormat | null {
  if (!value || typeof value !== "object") return null;
  const { style, locale, hour12 } = value as Record<string, unknown>;
  if (style === "iso") return { style };
  if (
    style === "device" &&
    typeof locale === "string" &&
    /^[A-Za-z0-9-]{2,40}$/.test(locale) &&
    typeof hour12 === "boolean"
  )
    return { style, locale, hour12 };
  return null;
}

/**
 * Runs a view's page in an iframe and keeps it in step with the view's connection over the frame's channel
 * (frame-channel.ts, frame/): its copy of the view's data and its `window.coast`. Picking, the rings' places, the
 * scroll, and the page's look come back from the frame as reports; nothing here reaches into its document.
 */
/** A person as a page reads them: their icon's picture at its full address, which the page's policy lets it load. */
const withPicture = (person: Person): Person => ({ ...person, icon: pageIcon(person.icon, location.origin) });

export class ViewRenderer {
  private shown: ViewDocument | null = null;
  private source = "";
  /** Hears the shown view's `source` event while attached: the page is built again from the new source. */
  private readonly sourceChanged = () => this.loadSource();
  /** Whether the current page was stopped for loading another page in its frame. */
  private stopped = false;
  /** The gamepads the runtime's page sees, as last passed to the view, whose sandboxed frame can't read them in WebKit. */
  private pads = "";
  private padFrame = 0;
  /** What the frame last said of the elements with rings, by selector, and the selectors it was asked to follow. */
  private seen = new Map<string, Seen | null>();
  private followed: string[] = [];
  private overlayBottom = 0;
  private overlayTop = 0;
  private scroll = { x: 0, y: 0 };
  identity: Identity = { id: "you", name: "You", color: "#b7de9f" };
  /** The view's data, when it has a connection: `coast.data`, `coast.people`, and `coast.me`. */
  private data: DataHost | null = null;
  context: PageContext = { view: "", chat: null, org: null };
  /** A section asked for before the page's channel opened, which it opens with once it does (`openSection`). */
  private pendingSection: NavEntry | null = null;
  /** The sections opened on this screen, kept so the page reopens at the same one when its source changes. */
  private nav: NavEntry[] = [];
  /** The stack last reported to the Coast app, so a change of data that names no new section says nothing. */
  private toldNav = "";
  private connection: "live" | "offline" = "live";
  liveConnection: LiveConnection | null = null;
  /** The current page's channel; a page built again gets a new one, and the old one answers nothing more. */
  private channel: FrameChannel | null = null;
  /** What the frame's copy holds, by partition and collection, so only what changed goes to it. */
  private bootVersion = 0;
  private dataDirty = false;
  private dataQueued = 0;
  /** Receives what the page asks of the Coast app around it. */
  onRequest: (request: PageRequest) => void = noop;
  /** The links outside Coast the view has opened, which it may only after a tap. */
  private outside = new OutsideLinks();
  selection: Selection | null = null;
  participants: Participant[] = [];
  picking = false;
  onPick: (selection: Selection) => void = noop;
  /** Escape while picking. */
  onUnpick: () => void = noop;
  /** Whether the page looks dark or light, and its background as `#rrggbb` when opaque, as its frame last said; null until it has. */
  appearance: "dark" | "light" | null = null;
  background: string | null = null;
  onAppearance: () => void = noop;
  /** How tall the page's content is, in CSS pixels, as its frame measures it; null until it has. */
  contentHeight: number | null = null;
  onHeight: () => void = noop;
  /** Copies a view's text, which the runtime does for it after a tap: the page's frame has no clipboard. */
  onCopy: (text: string) => unknown = (text) => navigator.clipboard?.writeText(text);
  onLoaded: () => void = noop;
  /**
   * Whether the page is in view, as the Coast app says: while it isn't, its data's changes wait, and when it is again it
   * hears them at once, as one change.
   */
  visible = true;
  setVisible(visible: boolean) {
    if (visible === this.visible) return;
    this.visible = visible;
    this.channel?.push("visible", visible);
    if (visible) this.flushData();
  }
  /**
   * Refresh from the view's ⋯ menu: the page reads its data again in place when it listens (`coast.on("refresh")`, as
   * the kit's parts do), and otherwise asks to be loaded again from its source, which only a Refresh lets it do.
   */
  private refreshing = false;
  refresh() {
    if (!this.channel?.open) return;
    this.refreshing = true;
    this.channel.push("refresh");
  }
  /** Taps the first element a selector matches, as a render's steps ask (view_render's `steps`); `onTapped` hears whether one did. */
  tap(selector: string) {
    this.channel?.push("tap", selector);
  }
  onTapped: (found: boolean) => void = noop;
  /** Scrolls the page to its end (`bottom`) or to the element a selector names, as a render asks (view_render's `scroll`). */
  scrollTo(target: string) {
    this.channel?.push("scroll.to", target);
  }
  /** The page has drawn what it shows first: its scripts have run and it has painted (`frame/drawn.ts`). Once per page. */
  onDrawn: () => void = noop;
  private toldDrawn = false;
  private drawn() {
    if (!this.toldDrawn && !this.stopped) {
      this.toldDrawn = true;
      this.onDrawn();
    }
  }
  /** The layout the page is shown in; read by the page as `coast.layout` when it loads. */
  layout: Layout = "full";
  /** The viewer's accent and the text color that reads on it: `--coast-accent`, `--coast-on-accent`, `coast.accent`. */
  accent = "#0a84ff";
  /** How the page writes dates and times: ISO until the Coast app says otherwise. */
  format: DateFormat = { style: "iso" };
  onAccent = "#ffffff";
  /** Receives errors, failed loads, and console errors from the view's page. */
  onPageError: ((error: PageError) => void) | null = null;
  /**
   * The page's frame. Each page gets a frame element of its own, made from the one the runtime's page has, so the first
   * load of an element is its page and any later load is the view navigating it; no late event of an older page can
   * reach the new one.
   */
  iframe: HTMLIFrameElement;
  private readonly blank: HTMLIFrameElement;
  constructor(
    iframe: HTMLIFrameElement,
    readonly layer: HTMLElement,
  ) {
    this.iframe = iframe;
    this.blank = iframe.cloneNode(false) as HTMLIFrameElement;
    this.blank.removeAttribute("srcdoc");
    window.addEventListener("resize", () => this.drawOverlays());
    // The page's hello, which only the current page's channel answers.
    window.addEventListener("message", (event) => {
      this.channel?.accept(event);
    });
    for (const type of ["gamepadconnected", "gamepaddisconnected"])
      window.addEventListener(type, () => this.watchPads());
  }
  /** How many lines the boot script and stock style put before the view's source, so a line in the frame reads as a line of the view's source. */
  private injected = { after: 0, lines: 0 };
  private sourceLine(error: PageError): PageError {
    if (!error.line || (error.source && error.source !== "about:srcdoc")) return error;
    const { after, lines } = this.injected;
    if (error.line <= after) return error;
    if (error.line > after + lines) return { ...error, line: error.line - lines };
    // An error inside what Coast put in: the stock kit or the page API, not the view's own code.
    const { line: _line, ...rest } = error;
    return { ...rest, source: "coast" };
  }
  /** The people the view lists, marked here when one of their devices has it open, a picture at its full address. */
  private people(): Person[] {
    const visitors = this.participants.flatMap((person) =>
      person.personId ? [{ id: person.personId, name: person.name }] : [],
    );
    return (this.data?.people(new Set(visitors.map((visitor) => visitor.id)), visitors) ?? []).map(withPicture);
  }
  private me(): Person {
    const { id, name } = this.identity;
    const known = this.data?.me({ id, name });
    return (
      (known && withPicture(known)) ?? {
        id,
        name,
        short: name.split(/\s+/)[0] || name,
        color: this.identity.color,
        kind: "person",
        role: null,
        status: null,
        here: true,
        icon: null,
      }
    );
  }
  /** People and who else is here, sent to the page only when they change: each one redraws a view built with the kit. */
  private tellPeople() {
    const people = JSON.stringify({ people: this.people(), me: this.me() });
    if (people !== this.toldPeople && this.channel?.open) {
      this.toldPeople = people;
      this.channel.push("people", JSON.parse(people));
    }
  }
  private tellOthers() {
    const others = JSON.stringify(this.liveOthers());
    if (others !== this.toldOthers && this.channel?.open) {
      this.toldOthers = others;
      this.channel.push("others", JSON.parse(others));
    }
  }
  private toldPeople = "";
  private toldOthers = "";
  /** Who else has the page open, with their `coast.live` state, as `coast.live.others()` reads it. */
  private liveOthers() {
    return this.participants
      .filter((person) => person.clientId !== this.shown?.clientID)
      .map((person) => ({ person: this.personOf(person), state: person.live ?? null }));
  }
  /** Someone with the view open, as `coast.people` lists them when it does. */
  private personOf(participant: Participant): Person {
    const listed = participant.personId
      ? this.people().find((person) => person.id === participant.personId)
      : undefined;
    return (
      listed ?? {
        id: participant.personId ?? String(participant.clientId),
        name: participant.name,
        short: participant.name.split(/\s+/)[0] || participant.name,
        color: participant.color,
        kind: "person",
        role: null,
        status: null,
        here: true,
        icon: null,
      }
    );
  }
  /** The data changed: the frame's copy follows within a frame, and at once before any answer that depends on it. */
  private tellData() {
    this.dataDirty = true;
    if (!this.dataQueued && this.channel?.open)
      this.dataQueued = requestAnimationFrame(() => {
        this.dataQueued = 0;
        this.flushData();
      });
    // A section's record may only now have arrived to name it.
    if (this.nav.length) this.tellNav();
  }
  private flushData() {
    if (this.dataQueued) {
      cancelAnimationFrame(this.dataQueued);
      this.dataQueued = 0;
    }
    const channel = this.channel,
      data = this.data;
    if (!channel?.open || !data || !this.dataDirty || !this.visible) return;
    this.dataDirty = false;
    const change = data.changes();
    if (change) channel.push("data", change);
  }
  /**
   * The sections open on this screen, for the Coast app's header and Back (`view.nav`). A section the view named keeps
   * its title; one it didn't takes whatever the record it shows calls itself, as the Coast app's header names it.
   */
  private tellNav() {
    const stack = this.nav.map((entry) => {
      if (entry.title || !this.data) return entry;
      const shows = this.data.manifest().sections[entry.name]?.shows,
        id = entry.params.id;
      const rec = shows && id ? this.data.get(shows, id) : undefined;
      const name = rec
        ? [rec.title, rec.name, rec.species, rec.place, rec.text].find((value) => typeof value === "string" && value)
        : undefined;
      return typeof name === "string" ? { ...entry, title: name.slice(0, 80) } : entry;
    });
    const told = JSON.stringify(stack);
    if (told === this.toldNav) return;
    this.toldNav = told;
    this.onRequest({ type: "view.nav", stack });
  }
  /** A `coast.live.send` from someone else with the view open. */
  receiveLive(type: string, data: unknown, from: string) {
    const sender = this.participants.find((person) => person.clientId === from);
    this.channel?.push("live", { type, data, from: sender ? this.personOf(sender) : null });
  }
  setConnection(connected: boolean) {
    const next = connected ? "live" : "offline";
    if (next === this.connection) return;
    this.connection = next;
    this.channel?.push("connection", next);
  }
  /**
   * A section the view opens on this screen: a link, or a person's line from their card. A page that is still loading
   * drops what is pushed to it, so a section that comes before its channel opens waits for it: as when a notification
   * opens a chat and its alert's link opens a view the chat is still loading.
   */
  openSection(section: NavEntry) {
    if (!SECTION.test(section?.name)) return;
    const entry: NavEntry = {
      name: section.name,
      params: strings(section.params),
      ...(section.title ? { title: String(section.title) } : {}),
    };
    if (this.channel?.open) this.channel.push("open", entry);
    else this.pendingSection = entry;
  }
  /** How many sections are open on this screen. */
  get navDepth(): number {
    return this.nav.length;
  }
  /** Back from the Coast app's own control: the page's last section closes, or at its start the page asks to leave. */
  back() {
    if (this.channel?.open) this.channel.push("back");
    else this.onRequest({ type: "view.back" });
  }
  /**
   * `coast.open`: a section of this view opens in place, whether it comes as `#issue?id=…`, as this view's path, or as
   * the link to share it; a record opens the section that shows it, in this chat, and another view's record from `uses`
   * in that view; a person opens their card; anything else is a link the Coast app opens, kept relative when it is this
   * server's, and a link outside Coast only after a tap (docs/views.md#5-navigation-sections-and-links).
   */
  private open(target: unknown) {
    const section = (name: string, params: Record<string, string>, title?: unknown) =>
      this.channel?.push("open", { name, params, ...(typeof title === "string" ? { title } : {}) });
    if (typeof target === "string") {
      const resolved = resolveLink(target, this.data?.info?.links, location.origin);
      if (resolved && "open" in resolved) {
        if (!this.outside.allow(resolved.open, tapped()))
          throw refuse("A link outside Coast opens after a tap, one a second.", "forbidden");
        this.onRequest({ type: "view.open", url: resolved.open });
      } else if (resolved?.section.name) section(resolved.section.name, resolved.section.params);
      // This view's own start: back to it from any section open here.
      else if (resolved) this.channel?.push("home");
      return;
    }
    if (!target || typeof target !== "object") return;
    const value = target as Record<string, unknown>;
    if (typeof value.person === "string") {
      this.onRequest({ type: "view.person", id: value.person, line: null });
      return;
    }
    if (typeof value.section === "string") {
      if (SECTION.test(value.section)) section(value.section, strings(value.params), value.title);
      return;
    }
    if (typeof value.id !== "string") return;
    const found = this.data?.locate(value.id);
    // Another view's record opens in that view.
    if (found?.link !== undefined) {
      const link = this.otherLink(found, value.id);
      if (link) this.open(link);
      return;
    }
    // This view's own records open here: in a chat its chat's, and in the home every chat's it reads.
    if (found?.section) section(found.section, { id: value.id }, value.title);
  }
  /** The link to another view's record that this view reads through `uses`: at the section of that view that shows it. */
  private otherLink(found: { section: string | null; link?: string | null }, id: string): string | null {
    return shareLink(found.link, found.section ? { section: found.section, params: { id } } : null, () => null);
  }
  /** Changes the accent in place: the page's CSS variables follow, and `coast.on` reports it. */
  setAccent(accent: string, onAccent: string) {
    if (!isColor(accent) || !isColor(onAccent)) return;
    this.accent = accent;
    this.onAccent = onAccent;
    this.channel?.push("accent", { accent, onAccent });
  }
  /** The face the Coast app sets views in (src/shared/fonts.ts); the page's style follows in place, and `coast.on` reports it. */
  font: Face = "system";
  /** Whether the page shown says `font: "own"`, which gets the variables and no rule. */
  private ownFont = false;
  setFont(face: unknown) {
    if (!isFace(face) || face === this.font) return;
    this.font = face;
    this.pushType();
  }
  /**
   * The Coast app's text sizes (src/shared/fonts.ts), which on iPhone and iPad follow the person's text size; until it
   * sends them, and with no Coast app, the platform's at its default: the Mac's in the desktop layout, iPhone's otherwise.
   */
  private text: PageText | null = null;
  setTextSizes(value: unknown) {
    const text = pageTextOf(value);
    if (!text || JSON.stringify(text) === JSON.stringify(this.text)) return;
    this.text = text;
    this.pushType();
  }
  /** The page's type as CSS: the face's style and the text sizes. */
  private typeStyle(): string {
    const text = this.text ?? defaultText(this.layout === "desktop" ? "mac" : "ios");
    return fontStyle(this.font, location.origin, this.ownFont) + textStyle(text);
  }
  /** The face and the sizes changed: the page's first style follows in place, and `coast.on` reports the face. */
  private pushType() {
    this.channel?.push("font", { font: fontOf(this.font), style: this.typeStyle() });
  }
  /** How the page writes dates and times; the page reloads so what it drew follows (`coast.format`). */
  setFormat(value: unknown) {
    const format = dateFormat(value);
    if (!format || JSON.stringify(format) === JSON.stringify(this.format)) return;
    this.format = format;
    if (this.shown) {
      this.source = "";
      this.loadSource();
    }
  }
  /** Shows the page in another layout; it reloads so `coast.layout` reads the new one. */
  setLayout(layout: "half" | "full" | "desktop") {
    if (layout === this.layout) return;
    this.layout = layout;
    if (this.shown) {
      this.source = "";
      this.loadSource();
    }
  }
  detach() {
    this.shown?.removeEventListener("source", this.sourceChanged);
    this.shown = null;
    this.source = "";
    if (this.data) {
      this.data.onChange = noop;
      this.data.onInfo = noop;
    }
    this.data = null;
    this.nav = [];
    this.liveConnection = null;
    this.selection = null;
    this.participants = [];
    this.picking = false;
    this.seen.clear();
    this.followed = [];
    this.appearance = null;
    this.background = null;
    this.layer.replaceChildren();
    this.channel?.close();
    this.channel = null;
    if (this.dataQueued) cancelAnimationFrame(this.dataQueued);
    this.dataQueued = 0;
    this.dataDirty = false;
    this.place("", null);
  }
  /**
   * Puts a new frame element in place of the last, holding `content`. Its srcdoc is set before it's in the page, so it
   * never loads an empty page first. `onLoad` hears each of its loads, counted.
   */
  private place(content: string, onLoad: ((loads: number) => void) | null) {
    const frame = this.blank.cloneNode(false) as HTMLIFrameElement;
    frame.srcdoc = content;
    let loads = 0;
    if (onLoad)
      frame.addEventListener("load", () => {
        if (this.iframe === frame) onLoad(++loads);
      });
    this.iframe.replaceWith(frame);
    this.iframe = frame;
    return frame;
  }
  /** Shows a view: its document, its data when it has a connection, and the sections it opens at. */
  attach(view: ViewDocument, data: DataHost | null = null, nav: NavEntry[] = []) {
    this.shown?.removeEventListener("source", this.sourceChanged);
    this.shown = view;
    view.addEventListener("source", this.sourceChanged);
    if (this.data && this.data !== data) {
      this.data.onChange = noop;
      this.data.onInfo = noop;
    }
    this.data = data;
    this.pendingSection = null;
    this.nav = nav.filter((entry) => SECTION.test(entry?.name));
    // A section the view opens at, from a link, shows in the Coast app's header with Back like any other.
    this.toldNav = "";
    if (this.nav.length) this.tellNav();
    if (data) {
      data.onChange = () => {
        if (this.data === data) this.tellData();
      };
      data.onInfo = () => {
        if (this.data === data) this.tellPeople();
      };
    }
    this.source = "";
    this.selection = null;
    this.participants = [];
    this.scroll = { x: 0, y: 0 };
    this.appearance = null;
    this.background = null;
    this.layer.replaceChildren();
    this.loadSource();
  }
  setParticipants(participants: Participant[]) {
    this.participants = participants;
    this.tellPeople();
    this.tellOthers();
    this.drawOverlays();
  }
  private loadSource() {
    if (!this.shown) return;
    const html = this.shown.source;
    if (!html || html === this.source) return;
    this.source = html;
    this.stopped = false;
    this.refreshing = false;
    this.toldDrawn = false;
    this.seen.clear();
    this.followed = this.follow();
    // The page's channel, bound to this render: its data, its generation and nonce.
    const data = this.data;
    this.channel?.close();
    // The page runs in an opaque origin, so its hello comes from "null".
    // Bound to the frame element this page gets, which is placed once the page is put together.
    let placed: HTMLIFrameElement | null = null;
    const channel: FrameChannel = new FrameChannel(
      {
        get contentWindow() {
          return placed?.contentWindow ?? null;
        },
      },
      (method, args) => this.call(channel, data, method, args),
      ["null"],
    );
    channel.onOpen = () => this.opened(channel, data);
    channel.beforeReply = () => this.flushData();
    this.channel = channel;
    if (this.dataQueued) cancelAnimationFrame(this.dataQueued);
    this.dataQueued = 0;
    this.dataDirty = false;
    // The page starts with everything it may read, so the view's scripts see data the moment they run.
    const snapshot = data?.snapshot() ?? EMPTY;
    data?.handed(snapshot);
    this.bootVersion = snapshot.version;
    const kept = data?.answers() ?? { answers: {}, cacheable: [] };
    const boot: FrameBoot = {
      generation: channel.generation,
      nonce: channel.nonce,
      init: this.pageInit(),
      data: snapshot,
      answers: kept.answers,
      cacheable: kept.cacheable,
      picking: this.picking,
      follow: this.followed,
      scroll: this.scroll,
    };
    // The page starts with these, so only a change goes to it once it connects.
    this.toldPeople = JSON.stringify({ people: boot.init.people, me: boot.init.me });
    this.toldOthers = "[]";
    // In a function, so the bundle's `coastFrame` stays out of the page's globals.
    const start = `<script data-coast-boot>(function(){${frame.code.replace(/<\/script/gi, "<\\/script")}\ncoastFrame.start(${embed(boot)})})()</script>`;
    // The stock style and kit come right after window.coast, which the kit builds on, and before the view's own styles.
    // A source without a valid manifest gets the stock style and no network.
    let style: "coast" | "own" = "coast",
      network: string[] = [],
      font: "coast" | "own" = "coast";
    try {
      ({ style, network, font } = readManifest(html));
    } catch {
      /* as above */
    }
    this.ownFont = font === "own";
    // The Coast app's face, its files from the view's server (docs/views.md#4-layouts), before the view's own styles.
    const fontTag = `<style data-coast-font>${this.typeStyle()}</style>${PAGE_STYLE}`;
    const stocked = withStock(html, style);
    const posted = this.context.reach === "posted";
    const policy = pagePolicy(posted ? [] : network, location.origin);
    // What Coast puts in goes before the view's source, so the parser makes it the first thing in the head whatever the
    // source holds: a <head> in a comment, no head, scripts first. The policy comes first, so it covers everything
    // after it. The view's own doctype, after it, is ignored, so the page stays in standards mode; its line numbers
    // move by the lines put in front.
    const prefix = `<!doctype html><meta http-equiv="Content-Security-Policy" content="${attribute(policy)}">${start}${fontTag}${stocked.head}`;
    const content = prefix + stocked.html.replace(/^\uFEFF/, "");
    const check = new DOMParser().parseFromString(content, "text/html");
    const [first, second] = check.head.children;
    if (
      check.compatMode !== "CSS1Compat" ||
      first?.getAttribute("http-equiv") !== "Content-Security-Policy" ||
      first.getAttribute("content") !== policy ||
      !second?.hasAttribute("data-coast-boot")
    ) {
      this.onPageError?.({ kind: "load", message: "The view could not be put together safely, so it was not shown." });
      this.place("", () => this.onLoaded());
      return;
    }
    this.injected = { after: 0, lines: prefix.split("\n").length - 1 };
    placed = this.place(content, (loads) => this.loaded(loads));
  }
  /** The page has its port: what changed while it loaded, and everything again when its data is older than the connection's. */
  private opened(channel: FrameChannel, data: DataHost | null) {
    if (this.channel !== channel) return;
    if (this.pendingSection) {
      channel.push("open", this.pendingSection);
      this.pendingSection = null;
    }
    if (data && data.version !== this.bootVersion) {
      const snapshot = data.snapshot();
      data.handed(snapshot);
      this.dataDirty = false;
      channel.push("data", { version: snapshot.version, ready: snapshot.ready, full: snapshot } satisfies DataChange);
    }
    this.tellPeople();
    this.tellOthers();
    channel.push("connection", this.connection);
    channel.push("accent", { accent: this.accent, onAccent: this.onAccent });
    channel.push("overlay", { top: this.overlayTop, bottom: this.overlayBottom });
    // Picking and rings may have changed while the page loaded.
    channel.push("picking", this.picking);
    channel.push("follow", this.followed);
    if (this.pads) channel.push("gamepads", JSON.parse(this.pads));
  }
  /** A call from the page, answered for the render it came from and refused once that render is gone. */
  private call(channel: FrameChannel, data: DataHost | null, method: string, args: unknown): unknown {
    if (this.channel !== channel) throw refuse("This page has been replaced.");
    const value = (args && typeof args === "object" && !Array.isArray(args) ? args : {}) as Record<string, unknown>;
    const text = (input: unknown, max = 200) => (typeof input === "string" ? input.slice(0, max) : "");
    // A page's message names one of FRAME_CALLS or FRAME_NOTICES, each with its case; anything else is refused.
    const name = method as FrameCall | FrameNotice;
    // A view in a message changes nothing on its own: its writes, posts, and actions wait for a tap in it, as copying
    // does, so one anyone posted can't act as each viewer who scrolls past it.
    if (this.context.reach === "posted" && ACTS.has(name) && !tapped())
      throw refuse(
        data?.asked
          ? "A question's view saves and answers only after a tap in it."
          : "A view in a message saves and acts only after a tap in it.",
        "forbidden",
      );
    switch (name) {
      case "write": {
        if (!data) throw refuse("This view has no data yet.");
        const kind = WRITES.find((word) => word === value.kind);
        if (!kind || typeof value.collection !== "string") throw refuse("That is not a write.", "invalid");
        const options =
          value.options && typeof value.options === "object" ? (value.options as Record<string, unknown>) : {};
        return data.write(
          value.collection,
          kind,
          typeof value.id === "string" ? value.id : null,
          value.value as never,
          typeof options.chat === "string" ? { chat: options.chat } : {},
        );
      }
      case "history":
        return data ? data.history(text(value.collection), text(value.id)) : [];
      case "query": {
        if (!data) throw refuse("This view has no data yet.");
        const params =
          value.params && typeof value.params === "object" && !Array.isArray(value.params)
            ? (value.params as Record<string, unknown>)
            : {};
        return data.query(text(value.name, 40), params);
      }
      case "call": {
        if (!data) throw refuse("This view has no data yet.");
        const params =
          value.params && typeof value.params === "object" && !Array.isArray(value.params)
            ? (value.params as Record<string, unknown>)
            : {};
        const options =
          value.options && typeof value.options === "object" ? (value.options as Record<string, unknown>) : {};
        return data.call(text(value.name, 40), params, typeof options.chat === "string" ? { chat: options.chat } : {});
      }
      // An update or an alert, posted as the viewer via the view (docs/views.md#6-the-page-api).
      case "post":
        if (!data) throw refuse("This view has no data yet.");
        return data.post(value.kind, value.item);
      // The viewer's answer to the question this view was asked with (docs/agent.md#asking-you), and its label.
      case "respond":
        if (!data) throw refuse("This view has no data yet.");
        return data.respond(value.value, value.label);
      case "open":
        this.open(args);
        return;
      case "nav":
        this.nav = stackOf(args);
        this.tellNav();
        return;
      // A section opened deeper as a screen of its own, at this page's stack and the section; the view's start, from a
      // screen of its own; and Back, which closes that screen.
      case "screen": {
        const stack = stackOf(value.stack);
        const presentation = value.as === "sheet" ? "sheet" : "screen";
        if (stack.length) this.onRequest({ type: "view.screen", stack, presentation });
        return;
      }
      case "home":
        this.onRequest({ type: "view.home" });
        return;
      case "back":
        this.onRequest({ type: "view.back" });
        return;
      case "title":
        this.onRequest({
          type: "view.title",
          text: text(value.text, 80),
          ...(value.subtitle ? { subtitle: text(value.subtitle, 80) } : {}),
        });
        return;
      case "person":
        this.onRequest({ type: "view.person", id: text(value.id, 80), line: personLineOf(value.line) });
        return;
      // Checked first, so a value too large is refused whether or not there is a connection to send it to.
      case "live.set": {
        const live = liveValue(args);
        this.liveConnection?.setLive(live);
        return;
      }
      case "live.send": {
        if (typeof value.type !== "string" || !LIVE_TYPE.test(value.type))
          throw refuse("A coast.live message type is a short name.", "invalid");
        const data = liveValue(value.data);
        this.liveConnection?.sendLive(value.type, data);
        return;
      }
      case "error": {
        const kind =
          (["error", "rejection", "console", "load"] as const).find((word) => word === value.kind) ?? "error";
        const line =
          typeof value.line === "number" && Number.isInteger(value.line) && value.line > 0 ? value.line : undefined;
        this.onPageError?.(
          this.sourceLine({
            kind,
            message: text(value.message, 300),
            ...(typeof value.source === "string" ? { source: text(value.source) } : {}),
            ...(line ? { line } : {}),
          }),
        );
        return;
      }
      // What the frame sees, for drawing: a picked element, the elements with rings, the scroll, and the page's look.
      case "copy": {
        if (typeof value.text !== "string" || value.text.length > 100_000)
          throw refuse("Copying takes text of up to 100,000 characters.", "invalid");
        // Only from a tap or a key press in the view, so a view can't fill the clipboard on its own.
        if (!tapped()) throw refuse("Copying needs a tap.", "forbidden");
        return this.onCopy(value.text);
      }
      case "drawn":
        this.drawn();
        return;
      case "tapped":
        this.onTapped(value.found === true);
        return;
      // A page that doesn't hear Refresh is loaded again from its source, once per Refresh.
      case "reload":
        if (!this.refreshing) return;
        this.refreshing = false;
        this.source = "";
        this.loadSource();
        return;
      case "pick": {
        const picked = this.picking ? pickedOf(args) : null;
        if (picked) this.onPick(this.selectionOf(picked));
        return;
      }
      case "unpick":
        if (this.picking) this.onUnpick();
        return;
      case "seen":
        this.see(args);
        return;
      case "scroll":
        this.scroll = { x: Math.max(0, finite(value.x)), y: Math.max(0, finite(value.y)) };
        return;
      case "height": {
        const height = Math.min(100_000, Math.max(0, Math.round(finite(value.height))));
        if (height && height !== this.contentHeight) {
          this.contentHeight = height;
          this.onHeight();
        }
        return;
      }
      case "appearance": {
        const look = value.look === "dark" || value.look === "light" ? value.look : null;
        const background = isColor(value.background) ? value.background.toLowerCase() : null;
        if (look !== this.appearance || background !== this.background) {
          this.appearance = look;
          this.background = background;
          this.onAppearance();
        }
        return;
      }
      default: {
        const unknown: never = name;
        throw refuse(`There is no call ${text(unknown, 40)}.`, "invalid");
      }
    }
  }
  /** What `window.coast` starts with: who and where, the view's data so far, and the screen's sections. */
  private pageInit(): PageInit {
    const info = this.data?.info ?? null;
    const name = this.data?.manifest().name ?? "";
    // The home names no chat and reads several; a view in a chat reads that one (docs/views.md#6-the-page-api).
    const home = this.context.scope === "home" || info?.scope === "home";
    const chat =
      !home && info?.chat
        ? {
            id: info.chat,
            title: this.context.chat?.title || name,
            icon: pageIcon(this.context.chat?.icon, location.origin),
          }
        : null;
    const readable = new Set(info?.chats ?? []);
    const chats = home
      ? (this.context.chats ?? [])
          .filter((entry) => !readable.size || readable.has(entry.id))
          .map((entry) => ({ ...entry, icon: pageIcon(entry.icon, location.origin) }))
      : chat
        ? [{ ...chat, member: true }]
        : [];
    return {
      me: this.me(),
      people: this.people(),
      layout: this.layout,
      accent: this.accent,
      onAccent: this.onAccent,
      font: fontOf(this.font),
      maps: `${location.origin}${MAPS_PATH}`,
      format: this.format,
      overlay: { top: this.overlayTop, bottom: this.overlayBottom },
      scope: home ? "home" : "chat",
      chat,
      org: this.context.org,
      nav: this.nav,
      presents: this.context.presents ?? [],
      visible: this.visible,
      connection: this.connection,
      view: { id: this.context.view, name: home ? this.context.name || name : name, chats },
      reach: this.context.reach ?? "full",
    };
  }
  /**
   * The frame element loaded: the first time, its page has, with its scripts run, the edits applied, and the scroll
   * restored. Any later load is another page in the frame: the view navigated it, which the policies above and the
   * Coast app block where they can.
   */
  private loaded(loads: number) {
    if (!this.shown || !this.source || this.stopped) return;
    if (loads > 1) {
      this.stop();
      return;
    }
    // The page says when it has drawn (`frame/drawn.ts`); a page whose word never comes shows a moment after it could have,
    // unless another page has taken its place by then.
    const channel = this.channel;
    setTimeout(() => {
      if (this.channel === channel) this.drawn();
    }, DRAWN_WAIT_MS + 500);
    this.drawOverlays();
    this.onLoaded();
  }
  private stop() {
    this.stopped = true;
    this.channel?.close();
    this.channel = null;
    this.onPageError?.({
      kind: "load",
      message: "The view opened another page in its frame, which views can't, so Coast stopped it.",
    });
    this.place(STOPPED, null);
  }
  private watchPads() {
    if (!this.padFrame) this.padFrame = requestAnimationFrame(() => this.readPads());
  }
  /** Passes the gamepads to the view when they change, every animation frame while one is connected. */
  private readPads() {
    this.padFrame = 0;
    const pads = [...(navigator.getGamepads?.() ?? [])].slice(0, 4).map(
      (pad) =>
        pad && {
          index: pad.index,
          id: pad.id.slice(0, 120),
          mapping: pad.mapping,
          connected: pad.connected,
          axes: [...pad.axes],
          buttons: pad.buttons.map((button) => ({
            pressed: button.pressed,
            touched: button.touched,
            value: button.value,
          })),
        },
    );
    const json = JSON.stringify(pads);
    if (json !== this.pads) {
      this.pads = json;
      this.channel?.push("gamepads", pads);
    }
    if (pads.some(Boolean)) this.watchPads();
  }
  /** A picked element as a selection: its selector, what it says, and where it is on this page. */
  private selectionOf(picked: Picked): Selection {
    const frame = this.iframe.getBoundingClientRect();
    const { selector, id, classes, role, tag, text, label, rect } = picked;
    return {
      selector,
      tag,
      text,
      label,
      ...(id ? { id } : {}),
      ...(classes ? { classes } : {}),
      ...(role ? { role } : {}),
      frame: "page-frame",
      rect: { x: rect.x + frame.x, y: rect.y + frame.y, width: rect.width, height: rect.height },
    };
  }
  /** The frame's report of the elements with rings, by selector: they're drawn again where it says they are. */
  private see(input: unknown) {
    if (!input || typeof input !== "object") return;
    const report = input as Record<string, unknown>;
    this.seen.clear();
    for (const selector of this.followed)
      if (Object.hasOwn(report, selector)) this.seen.set(selector, seenOf(report[selector]));
    this.drawOverlays();
  }
  setPicking(picking: boolean) {
    this.picking = picking;
    this.channel?.push("picking", picking);
  }
  setOverlayTop(px: number) {
    this.overlayTop = Math.max(0, Math.min(2000, Number(px) || 0));
    this.tellOverlay();
  }
  setOverlayBottom(px: number) {
    this.overlayBottom = Math.max(0, Math.min(2000, Number(px) || 0));
    this.tellOverlay();
  }
  /** The clearances as `coast.overlay`, with an `overlay` event, for layouts in JavaScript that no CSS variable can wake. */
  private tellOverlay() {
    this.channel?.push("overlay", { top: this.overlayTop, bottom: this.overlayBottom });
  }
  /** The selections drawn as rings: yours, other people's, and the agent's. */
  private rings() {
    const rings: { key: string; selection: Selection; name: string; color: string; local?: boolean }[] = [];
    if (this.selection)
      rings.push({ key: "local", selection: this.selection, name: "You", color: "#b7de9f", local: true });
    for (const person of this.participants) {
      if (person.clientId === this.shown?.clientID || !person.selection) continue;
      rings.push({
        key: String(person.clientId),
        selection: person.selection,
        name: `${person.name}${person.editing ? " · editing" : ""}`,
        color: isColor(person.color) ? person.color : "#8bcd91",
      });
    }
    return rings;
  }
  /** The selectors of the elements with rings, for the frame to follow. */
  private follow() {
    return [
      ...new Set(
        this.rings()
          .map((ring) => ring.selection.selector)
          .filter(isSelector),
      ),
    ];
  }
  /** Draws the rings where the frame last said their elements are, and asks it to follow any new ones. */
  drawOverlays() {
    const rings = this.rings();
    const follow = this.follow();
    if (follow.join("\n") !== this.followed.join("\n")) {
      this.followed = follow;
      this.channel?.push("follow", follow);
    }
    const active = new Set<string>();
    const offset = this.iframe.getBoundingClientRect();
    for (const item of rings) {
      const rect = this.seen.get(item.selection.selector)?.rect;
      if (!rect?.width || !rect.height || rect.y + rect.height < 0 || rect.y > offset.height) continue;
      active.add(item.key);
      let ring = this.layer.querySelector<HTMLElement>(`[data-ring="${CSS.escape(item.key)}"]`);
      if (!ring) {
        ring = document.createElement("div");
        ring.dataset.ring = item.key;
        const label = document.createElement("span");
        label.className = "ring-label";
        ring.append(label);
        this.layer.append(ring);
      }
      ring.className = `selection-ring${item.local ? " local" : ""}${item.key === "agent" ? " agent" : ""}${rect.y < 26 ? " bottom-label" : ""}`;
      ring.style.cssText = `left:${rect.x + offset.left - 3}px;top:${rect.y + offset.top - 3}px;width:${rect.width + 6}px;height:${rect.height + 6}px;--ring-color:${item.color}`;
      const label = ring.firstElementChild as HTMLElement;
      if (item.key === "agent") {
        // "Agent editing" with dots that cycle . .. ... while the run lasts.
        if (!label.querySelector(".dots")) {
          const dots = document.createElement("span");
          dots.className = "dots";
          dots.append(document.createElement("i"), document.createElement("i"), document.createElement("i"));
          label.replaceChildren(document.createTextNode("Agent editing"), dots);
        }
      } else if (label.textContent !== item.name) label.textContent = item.name;
    }
    for (const ring of [...this.layer.children]) if (!active.has((ring as HTMLElement).dataset.ring!)) ring.remove();
  }
}
